import React, { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  darken,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { formatISO } from "date-fns";
import { NavLink } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import Banner from "@/components/Banner";
import GreetingsMessage from "@/components/GreetingMessage";
import { graphql } from "@/gql";
import { SubscriptionStatus } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import ojieCryIcon from "@assets/icons/ojie-cry.png";
import ojieCry3xIcon from "@assets/icons/ojie-cry@3x.png";

import ActiveAndStrugglingClientsPage from "./components/ActiveAndStrugglingClientsList/ActiveAndStrugglingClientsPage";
import ClientMetrics from "./components/ClientMetrics/ClientMetrics";

const FullGrid = withStyles(Grid, {
  root: {
    height: "100%",
  },
});

const ScrollableY = withStyles(Box, {
  root: {
    overflow: "hidden",
    overflowY: "auto",
    height: "100%",
  },
});

const useStyles = makeStyles()((theme) => ({
  banner: {
    margin: theme.spacing(5, 0, 9.5, 5),
  },
  coachName: {
    alignSelf: "center",
    fontSize: theme.typography.pxToRem(28),
    fontWeight: 500,
    marginRight: 8,
  },
  mainContentWrapper: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    height: "100%",
  },
  middleContentWrapper: {
    display: "flex",
    flexDirection: "column",
    marginRight: 12,
  },
  greetingIcon: {
    height: 65,
    width: 65,
    alignSelf: "center",
    marginRight: 32,
    fontSize: 300,
  },
  greetingText: {
    alignSelf: "center",
    fontSize: theme.typography.pxToRem(28),
    fontWeight: 300,
    marginRight: 8,
  },
  manageSubscriptionButton: {
    flexShrink: 0,
    marginLeft: "auto",
  },
  suspendedDialog: {
    minWidth: 1255,
    backgroundColor: "#FF6262",
    color: colors.white,
  },
  suspendedDialogContent: {
    padding: theme.spacing(0, 8, 8),
  },
  suspendedDialogHead: {
    display: "flex",
    justifyContent: "flex-end",
  },
  suspendedDialogTitle: {
    margin: theme.spacing(5, 0, 7),
    fontSize: "1.75rem",
  },
  suspendedDialogDescription: {
    marginBottom: theme.spacing(7),
    fontWeight: 600,
    fontSize: 12,
    lineHeight: 1.25,
  },
  suspendedDialogButton: {
    marginBottom: theme.spacing(8),
    backgroundColor: "#0B1B26",
    color: colors.white,
    "&:hover": {
      backgroundColor: darken("#0B1B26", 0.2),
    },
  },
}));

const dashboardPageQuery = graphql(`
  query DashboardPage($currentDate: ISO8601Date!) {
    coachViewer {
      id
      profile {
        id
        fullName
      }
      trialWillEnd
      subscriptionSuspended
      subscriptionStatus
    }

    ...ActiveAndStrugglingClients
    ...ClientMetrics
  }
`);

const DashboardPage: React.FC = () => {
  const { classes } = useStyles();
  const currentDate = formatISO(new Date(), { representation: "date" });
  const { data, loading } = useQuery(dashboardPageQuery, {
    variables: { currentDate: currentDate },
  });
  const [trialWillEndBannerVisible, setTrialWillEndBannerVisible] =
    useState(false);
  const [suspendedDialogVisible, setSuspendedDialogVisible] = useState(false);
  const [suspendedBannerVisible, setSuspendedBannerVisible] = useState(false);

  const me = data?.coachViewer;

  useEffect(() => {
    if (me?.trialWillEnd) {
      setTrialWillEndBannerVisible(true);
    }
  }, [me?.trialWillEnd]);

  useEffect(() => {
    if (me?.subscriptionSuspended) {
      setSuspendedDialogVisible(true);
    }
  }, [me?.subscriptionSuspended]);

  const dismissSuspendedDialogIntoBanner = () => {
    setSuspendedDialogVisible(false);
    setSuspendedBannerVisible(true);
  };

  return (
    <>
      <Grid container className={classes.mainContentWrapper}>
        <FullGrid
          item
          xs={9}
          style={{ display: "flex", flexDirection: "column", paddingLeft: 4 }}
        >
          <ScrollableY>
            {trialWillEndBannerVisible && (
              <Banner
                className={classes.banner}
                color="secondary"
                renderButton={(props) => (
                  <Button {...props} component={NavLink} to="/subscription">
                    Manage subscription
                  </Button>
                )}
                onDismiss={() => setTrialWillEndBannerVisible(false)}
                title="Your free trial period is about to expire."
              />
            )}
            {me?.subscriptionStatus === SubscriptionStatus.PastDue && (
              <Banner
                className={classes.banner}
                color="error"
                description="Something went wrong and we couldn't process your payment. As we care for you and your client, we are not suspending your account until our retry period is finished. If you continue to have issues, kindly try another payment method."
                renderButton={() => (
                  <Button
                    className={classes.manageSubscriptionButton}
                    component={NavLink}
                    to="/subscription"
                  >
                    Manage subscription
                  </Button>
                )}
                renderLeft={() => <img src={ojieCryIcon} />}
                title="Payment failed"
              />
            )}
            {suspendedBannerVisible && (
              <Banner
                className={classes.banner}
                color="critical"
                description="Please settle your account subscription as soon as possible to continue your full access to NutriCoach application."
                renderButton={(props) => (
                  <Button {...props} component={NavLink} to="/subscription">
                    Manage subscription
                  </Button>
                )}
                renderLeft={() => <img src={ojieCryIcon} />}
                title="Payment failed"
              />
            )}
            <GreetingsMessage fullName={me?.profile?.fullName ?? "..."} />
            <ActiveAndStrugglingClientsPage loading={loading} />
          </ScrollableY>
        </FullGrid>

        <FullGrid item xs={3}>
          <ScrollableY>
            <ClientMetrics loading={loading} />
          </ScrollableY>
        </FullGrid>
        <Dialog
          classes={{ paper: classes.suspendedDialog }}
          open={suspendedDialogVisible}
        >
          <DialogTitle className={classes.suspendedDialogHead}>
            <IconButton
              aria-label="close"
              color="inherit"
              onClick={dismissSuspendedDialogIntoBanner}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent className={classes.suspendedDialogContent}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <img src={ojieCry3xIcon} />
              <Typography className={classes.suspendedDialogTitle} variant="h4">
                Payment Failed
              </Typography>
              <Typography className={classes.suspendedDialogDescription}>
                Please settle your account subscription as soon as possible to
                continue your full access to NutriCoach application.
              </Typography>
              <Button
                className={classes.suspendedDialogButton}
                component={NavLink}
                to="/subscription"
              >
                Manage subscription
              </Button>
              <Typography variant="caption">
                For concerns, Contact Us.
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      </Grid>
    </>
  );
};

export default DashboardPage;
